import { classifyScanError, nextScanRetryAt } from './scanSyncPolicy.js'

const DEFAULT_DB_NAME = 'camit-scan-outbox'
const DEFAULT_STORE_NAME = 'pending-scans'
const DB_VERSION = 1
const SYNCING_LOCK_MS = 20_000
export const SCAN_OUTBOX_SYNC_TAG = 'camit-scan-outbox'
export const SCAN_OUTBOX_SYNC_MESSAGE = 'CAMIT_SCAN_OUTBOX_SYNC_REQUEST'

function cloneValue(value) {
  if (typeof structuredClone === 'function') return structuredClone(value)
  return value
}

function mutationIdOf(value) {
  return typeof value === 'string' ? value : value?.clientMutationId
}

function assertMutationId(value) {
  const clientMutationId = mutationIdOf(value)
  if (!clientMutationId || typeof clientMutationId !== 'string') {
    throw new TypeError('clientMutationId est requis')
  }
  return clientMutationId
}

function toRecord(entry, previous) {
  const clientMutationId = assertMutationId(entry)
  const now = new Date().toISOString()
  return {
    ...previous,
    ...entry,
    clientMutationId,
    cartId: entry.cartId ?? previous?.cartId ?? null,
    draftPayload: entry.draftPayload ?? previous?.draftPayload ?? {},
    captureBlob: entry.captureBlob ?? previous?.captureBlob ?? null,
    additionalBlobs: entry.additionalBlobs ?? previous?.additionalBlobs ?? [],
    status: entry.status ?? previous?.status ?? 'pending',
    attempts: entry.attempts ?? previous?.attempts ?? 0,
    nextRetryAt: entry.nextRetryAt ?? previous?.nextRetryAt ?? null,
    lastError: entry.lastError ?? previous?.lastError ?? '',
    createdAt: previous?.createdAt ?? entry.createdAt ?? now,
    updatedAt: now,
  }
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'))
  })
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'))
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'))
  })
}

function openDatabase(indexedDB, dbName, storeName) {
  return new Promise((resolve, reject) => {
    let request
    try {
      request = indexedDB.open(dbName, DB_VERSION)
    } catch (error) {
      reject(error)
      return
    }
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(storeName)) {
        database.createObjectStore(storeName, { keyPath: 'clientMutationId' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('IndexedDB open failed'))
    request.onblocked = () => reject(new Error('IndexedDB open blocked'))
  })
}

export function createMemoryScanOutbox(initialEntries = []) {
  const records = new Map()
  for (const entry of initialEntries) {
    const record = toRecord(entry)
    records.set(record.clientMutationId, cloneValue(record))
  }

  return {
    kind: 'memory',

    async enqueue(entry) {
      const clientMutationId = assertMutationId(entry)
      const existing = records.get(clientMutationId)
      if (existing) return cloneValue(existing)
      const record = toRecord(entry)
      records.set(clientMutationId, cloneValue(record))
      return cloneValue(record)
    },

    async list() {
      return [...records.values()]
        .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
        .map(cloneValue)
    },

    async update(clientMutationIdOrEntry, patch = {}) {
      const clientMutationId = assertMutationId(clientMutationIdOrEntry)
      const existing = records.get(clientMutationId)
      if (!existing) return null
      const changes = typeof clientMutationIdOrEntry === 'string' ? patch : clientMutationIdOrEntry
      const record = toRecord({ ...changes, clientMutationId }, existing)
      records.set(clientMutationId, cloneValue(record))
      return cloneValue(record)
    },

    async remove(clientMutationIdOrEntry) {
      return records.delete(assertMutationId(clientMutationIdOrEntry))
    },

    async cleanupCancelled(clientMutationIdOrEntry) {
      return records.delete(assertMutationId(clientMutationIdOrEntry))
    },
  }
}

export function createScanOutbox({
  indexedDB = globalThis.indexedDB,
  dbName = DEFAULT_DB_NAME,
  storeName = DEFAULT_STORE_NAME,
  fallback = createMemoryScanOutbox(),
} = {}) {
  let databasePromise = null
  let useFallback = !indexedDB

  async function database() {
    if (useFallback) return null
    if (!databasePromise) {
      databasePromise = openDatabase(indexedDB, dbName, storeName).catch(() => {
        useFallback = true
        return null
      })
    }
    return databasePromise
  }

  async function withStore(mode, operation, fallbackOperation) {
    const db = await database()
    if (!db) return fallbackOperation()
    try {
      const transaction = db.transaction(storeName, mode)
      const store = transaction.objectStore(storeName)
      const completed = transactionDone(transaction)
      const [result] = await Promise.all([operation(store, transaction), completed])
      return result
    } catch {
      useFallback = true
      try {
        db.close()
      } catch {
        // Certains doubles IndexedDB n'implémentent pas close().
      }
      return fallbackOperation()
    }
  }

  return {
    get kind() {
      return useFallback ? 'memory' : 'indexeddb'
    },

    async enqueue(entry) {
      const clientMutationId = assertMutationId(entry)
      return withStore(
        'readwrite',
        async (store) => {
          const existing = await requestResult(store.get(clientMutationId))
          if (existing) return existing
          const record = toRecord(entry)
          await requestResult(store.add(record))
          return record
        },
        () => fallback.enqueue(entry),
      )
    },

    async list() {
      return withStore(
        'readonly',
        async (store) => {
          const records = await requestResult(store.getAll())
          return records.sort((left, right) => left.createdAt.localeCompare(right.createdAt))
        },
        () => fallback.list(),
      )
    },

    async update(clientMutationIdOrEntry, patch = {}) {
      const clientMutationId = assertMutationId(clientMutationIdOrEntry)
      return withStore(
        'readwrite',
        async (store) => {
          const existing = await requestResult(store.get(clientMutationId))
          if (!existing) return null
          const changes = typeof clientMutationIdOrEntry === 'string' ? patch : clientMutationIdOrEntry
          const record = toRecord({ ...changes, clientMutationId }, existing)
          await requestResult(store.put(record))
          return record
        },
        () => fallback.update(clientMutationIdOrEntry, patch),
      )
    },

    async remove(clientMutationIdOrEntry) {
      const clientMutationId = assertMutationId(clientMutationIdOrEntry)
      return withStore(
        'readwrite',
        async (store) => {
          const existing = await requestResult(store.get(clientMutationId))
          if (!existing) return false
          await requestResult(store.delete(clientMutationId))
          return true
        },
        () => fallback.remove(clientMutationId),
      )
    },

    async cleanupCancelled(clientMutationIdOrEntry) {
      return this.remove(clientMutationIdOrEntry)
    },
  }
}

function isConfirmedResult(result) {
  if (result && typeof result === 'object') {
    return Boolean(result.id && !String(result.id).startsWith('pending-'))
  }
  return Boolean(result)
}

export function createScanOutboxSynchronizer({
  outbox,
  submit,
  eventTarget = globalThis.window,
  network = globalThis.navigator,
  serviceWorker = globalThis.navigator?.serviceWorker,
  logger = console,
  classifyError = classifyScanError,
} = {}) {
  if (!outbox || typeof outbox.list !== 'function') throw new TypeError('outbox est requis')
  if (typeof submit !== 'function') throw new TypeError('submit est requis')

  let started = false
  let running = null

  const isOnline = () => network?.onLine !== false

  async function requestBackgroundSync() {
    try {
      const registration = await serviceWorker?.ready
      if (!registration?.sync?.register) return false
      await registration.sync.register(SCAN_OUTBOX_SYNC_TAG)
      return true
    } catch {
      return false
    }
  }

  async function run() {
    if (!isOnline()) {
      const pending = (await outbox.list()).length
      if (pending) await requestBackgroundSync()
      return { sent: 0, pending }
    }
    if (running) return running

    running = (async () => {
      let sent = 0
      const entries = await outbox.list()
      for (const entry of entries) {
        if (!isOnline()) break
        if (entry.status === 'saved' || entry.status === 'failed') continue
        if (entry.status === 'syncing') {
          const updated = Date.parse(entry.updatedAt || '') || 0
          if (updated && Date.now() - updated < SYNCING_LOCK_MS) continue
        }
        if (entry.nextRetryAt && Date.parse(entry.nextRetryAt) > Date.now()) continue
        await outbox.update(entry.clientMutationId, { status: 'syncing' })
        try {
          const result = await submit(entry, { clientMutationId: entry.clientMutationId })
          if (!isConfirmedResult(result)) {
            throw Object.assign(new Error('Enregistrement non confirmé'), { status: 503 })
          }
          await outbox.update(entry.clientMutationId, { status: 'saved', lastError: '' })
          await outbox.remove(entry.clientMutationId)
          sent += 1
        } catch (error) {
          const attempts = (entry.attempts || 0) + 1
          const kind = classifyError(error)
          if (kind === 'failed') {
            await outbox.update(entry.clientMutationId, {
              status: 'failed',
              attempts,
              lastError: error?.message || String(error),
              nextRetryAt: null,
            })
            continue
          }
          await outbox.update(entry.clientMutationId, {
            status: 'retry',
            attempts,
            lastError: error?.message || String(error),
            nextRetryAt: nextScanRetryAt(attempts),
          })
          logger?.warn?.('Synchronisation du scan différée', error)
          await requestBackgroundSync()
          break
        }
      }
      return { sent, pending: (await outbox.list()).length }
    })().finally(() => {
      running = null
    })

    return running
  }

  const onOnline = () => {
    serviceWorker?.controller?.postMessage?.({
      type: 'CAMIT_SCAN_OUTBOX_ONLINE',
    })
    void run()
  }

  const onServiceWorkerMessage = (event) => {
    if (event?.data?.type === SCAN_OUTBOX_SYNC_MESSAGE) void run()
  }

  return {
    sync: run,
    requestBackgroundSync,

    start() {
      if (started) return running
      started = true
      eventTarget?.addEventListener?.('online', onOnline)
      serviceWorker?.addEventListener?.('message', onServiceWorkerMessage)
      return run()
    },

    stop() {
      if (!started) return
      started = false
      eventTarget?.removeEventListener?.('online', onOnline)
      serviceWorker?.removeEventListener?.('message', onServiceWorkerMessage)
    },

    get started() {
      return started
    },
  }
}

export const scanOutbox = createScanOutbox()
export const enqueueScan = (entry) => scanOutbox.enqueue(entry)
export const listScans = () => scanOutbox.list()
export const updateScan = (clientMutationIdOrEntry, patch) =>
  scanOutbox.update(clientMutationIdOrEntry, patch)
export const removeScan = (clientMutationIdOrEntry) =>
  scanOutbox.remove(clientMutationIdOrEntry)
export const cleanupCancelledScan = (clientMutationIdOrEntry) =>
  scanOutbox.cleanupCancelled(clientMutationIdOrEntry)
